import type { FileNode } from '@ncoder/ui';

// Metadata types whose children live in named bundle subfolders.
// For these types, the Salesforce fullName IS the bundle folder name,
// so the tree needs two expansions: MetadataType → MetadataType/bundleName.
const BUNDLE_METADATA_TYPES = new Set([
  'LightningComponentBundle',
  'AuraDefinitionBundle',
]);

/**
 * Derives the IDs of ancestor folder nodes for a given file.
 *
 * Tree-loaded paths start with the metadataType:
 *   /ApexClass/MyClass.cls                           → ["ApexClass"]
 *   /LightningComponentBundle/homeLWC/homeLWC.js     → ["LightningComponentBundle", "LightningComponentBundle/homeLWC"]
 *
 * QuickOpen paths are Salesforce fullNames (no leading metadataType):
 *   "MyClass"   metadataType=ApexClass               → ["ApexClass"]
 *   "homeLWC"   metadataType=LightningComponentBundle → ["LightningComponentBundle", "LightningComponentBundle/homeLWC"]
 */
export function getAncestorFolderIds(file: FileNode): string[] {
  if (!file.path || !file.metadataType) return [];

  const segments = file.path.split('/').filter(Boolean);

  if (segments[0] === file.metadataType) {
    // Tree-loaded path: first segment is the metadataType folder.
    const folders: string[] = [segments[0]];
    if (segments.length >= 3) {
      folders.push(`${segments[0]}/${segments[1]}`);
    }
    return folders;
  }

  // QuickOpen path: Salesforce fullName — no leading metadataType.
  // For bundle types, fullName = bundle folder name → needs a second expansion.
  const folders: string[] = [file.metadataType];
  if (BUNDLE_METADATA_TYPES.has(file.metadataType)) {
    folders.push(`${file.metadataType}/${file.path}`);
  }
  return folders;
}

/**
 * Returns true when the file belongs to a bundle (LWC, Aura, etc.).
 * Determined purely by path depth — no hardcoded metadata type list.
 */
export function isBundleFile(file: FileNode): boolean {
  return file.path.split('/').filter(Boolean).length >= 3;
}

/**
 * Extracts the logical component name from a file name.
 *   "AccountCard.html"   → "AccountCard"
 *   "myTrigger.trigger"  → "myTrigger"
 *   "MyLWC"              → "MyLWC"  (folder names have no extension)
 */
export function extractComponentName(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
}

const EXTENSION_LABEL_MAP: Record<string, string> = {
  html: 'HTML',
  js: 'JS',
  css: 'CSS',
  xml: 'XML',
  cls: 'Apex',
  trigger: 'Trigger',
  cmp: 'CMP',
  evt: 'EVT',
  app: 'APP',
  intf: 'INTF',
};

/**
 * Returns a short uppercase label for a file based on its extension.
 *   "myLWC.html"        → "HTML"
 *   "myLWC.js-meta.xml" → "META"
 *   "myClass.cls"       → "Apex"
 */
export function getFiletypeLabel(fileName: string): string {
  if (fileName.endsWith('.js-meta.xml')) return 'META';
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_LABEL_MAP[ext] ?? ext.toUpperCase();
}
